import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createUseStyles } from 'react-jss';
import ReactMarkdown from 'react-markdown';
import { getAppURL } from 'shared/lib/clientSettings';
import { S, A, TONES, DARK } from '~/app/ui/tokens';

const useStyles = createUseStyles({
  // Floating button
  floatingButton: {
    position: 'fixed',
    bottom: '24px',
    right: '24px',
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    color: '#ffffff',
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 4px 20px rgba(99, 102, 241, 0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      transform: 'scale(1.05)',
      boxShadow: '0 6px 24px rgba(99, 102, 241, 0.5)',
    },
    '& .material-icons': {
      fontSize: '26px',
    },
  },
  // Drawer overlay
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.3)',
    zIndex: 999,
    opacity: 0,
    visibility: 'hidden',
    transition: 'opacity 0.3s ease, visibility 0.3s ease',
    [DARK]: { background: S.scrim },
  },
  overlayOpen: {
    opacity: 1,
    visibility: 'visible',
  },
  // Drawer panel
  drawer: {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    width: '480px',
    maxWidth: '100vw',
    background: A.surface,
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
    transform: 'translateX(100%)',
    transition: 'transform 0.3s ease, visibility 0.3s ease',
    boxShadow: '-4px 0 24px rgba(0, 0, 0, 0.15)',
    visibility: 'hidden',
  },
  drawerOpen: {
    transform: 'translateX(0)',
    visibility: 'visible',
  },
  // Header
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    color: '#ffffff',
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  headerIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: 'rgba(255, 255, 255, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& .material-icons': {
      fontSize: '22px',
    },
  },
  headerText: {
    '& h2': {
      margin: 0,
      fontSize: '16px',
      fontWeight: 600,
    },
    '& span': {
      fontSize: '12px',
      opacity: 0.85,
    },
  },
  closeButton: {
    background: 'rgba(255, 255, 255, 0.2)',
    border: 'none',
    borderRadius: '8px',
    padding: '8px',
    cursor: 'pointer',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.2s ease',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.3)',
    },
    '& .material-icons': {
      fontSize: '20px',
    },
  },
  // Messages container
  messagesContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    background: '#f8fafc',
    [DARK]: { background: S.chrome },
  },
  // Welcome message
  welcomeMessage: {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#64748b',
    '& .material-icons': {
      fontSize: '48px',
      color: '#6366f1',
      marginBottom: '16px',
    },
    '& h3': {
      margin: '0 0 8px 0',
      fontSize: '18px',
      color: '#0f172a',
    },
    '& p': {
      margin: '0 0 20px 0',
      fontSize: '14px',
      lineHeight: 1.5,
    },
    [DARK]: { color: S.muted, '& h3': { color: S.fg } },
  },
  exampleChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    justifyContent: 'center',
  },
  exampleChip: {
    padding: '8px 14px',
    fontSize: '13px',
    background: A.surface,
    border: '1px solid #e2e8f0',
    borderRadius: '20px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: '#6366f1',
      color: '#6366f1',
      background: '#f5f3ff',
    },
    [DARK]: {
      color: S.fg,
      borderColor: S.line,
      '&:hover': { borderColor: S.blue, color: TONES.blue.fg, background: A.rowSelected },
    },
  },
  // Message bubble
  messageBubble: {
    maxWidth: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  userMessage: {
    alignSelf: 'flex-end',
    '& $messageContent': {
      background: '#6366f1',
      color: '#ffffff',
      borderRadius: '16px 16px 4px 16px',
    },
  },
  assistantMessage: {
    alignSelf: 'flex-start',
    '& $messageContent': {
      background: A.surface,
      color: '#0f172a',
      borderRadius: '16px 16px 16px 4px',
      border: '1px solid #e2e8f0',
    },
    [DARK]: {
      '& $messageContent': {
        color: S.fg,
        borderColor: S.line,
        '& code, & pre': { background: S.chromeHover },
        '& blockquote': { borderLeftColor: S.line, color: S.muted },
        '& hr': { borderTopColor: S.line },
      },
    },
  },
  messageContent: {
    padding: '12px 16px',
    fontSize: '14px',
    lineHeight: 1.6,
    wordBreak: 'break-word',
    '& p': {
      margin: '0 0 8px 0',
      '&:last-child': {
        marginBottom: 0,
      },
    },
    '& strong': {
      fontWeight: 600,
    },
    '& em': {
      fontStyle: 'italic',
    },
    '& code': {
      background: 'rgba(0, 0, 0, 0.05)',
      padding: '2px 6px',
      borderRadius: '4px',
      fontSize: '13px',
      fontFamily: 'monospace',
    },
    '& pre': {
      background: 'rgba(0, 0, 0, 0.05)',
      padding: '12px',
      borderRadius: '6px',
      overflow: 'auto',
      margin: '8px 0',
      '& code': {
        background: 'transparent',
        padding: 0,
      },
    },
    '& ul, & ol': {
      margin: '8px 0',
      paddingLeft: '24px',
    },
    '& li': {
      margin: '4px 0',
    },
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      margin: '12px 0 8px 0',
      fontWeight: 600,
      '&:first-child': {
        marginTop: 0,
      },
    },
    '& h1': { fontSize: '20px' },
    '& h2': { fontSize: '18px' },
    '& h3': { fontSize: '16px' },
    '& h4': { fontSize: '15px' },
    '& h5': { fontSize: '14px' },
    '& h6': { fontSize: '14px' },
    '& blockquote': {
      borderLeft: '3px solid rgba(0, 0, 0, 0.1)',
      paddingLeft: '12px',
      margin: '8px 0',
      color: 'rgba(0, 0, 0, 0.7)',
    },
    '& hr': {
      border: 'none',
      borderTop: '1px solid rgba(0, 0, 0, 0.1)',
      margin: '12px 0',
    },
    '& a': {
      color: '#6366f1',
      textDecoration: 'underline',
      '&:hover': {
        color: '#4f46e5',
      },
    },
  },
  errorMessage: {
    '& $messageContent': {
      background: '#fef2f2',
      color: '#991b1b',
      border: '1px solid #fecaca',
    },
    [DARK]: {
      '& $messageContent': { background: TONES.red.bg, color: TONES.red.fg, borderColor: TONES.red.bg },
    },
  },
  // Code blocks in messages
  codeBlockContainer: {
    marginTop: '8px',
    borderRadius: '8px',
    overflow: 'hidden',
    border: '1px solid #e2e8f0',
    background: '#1e293b',
    [DARK]: { borderColor: S.line },
  },
  codeBlockHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 12px',
    background: '#0f172a',
    borderBottom: '1px solid #334155',
  },
  codeBlockLang: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#94a3b8',
    textTransform: 'uppercase',
  },
  codeBlockActions: {
    display: 'flex',
    gap: '8px',
  },
  applyButton: {
    padding: '4px 10px',
    fontSize: '11px',
    fontWeight: 600,
    background: '#22c55e',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    transition: 'background 0.2s ease',
    '&:hover': {
      background: '#16a34a',
    },
    '& .material-icons': {
      fontSize: '14px',
    },
  },
  codeBlockBody: {
    padding: '12px',
    overflow: 'auto',
    maxHeight: '200px',
    '& pre': {
      margin: 0,
      fontSize: '12px',
      fontFamily: "'Fira Code', 'Monaco', monospace",
      color: '#e2e8f0',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
    },
  },
  // Loading indicator
  loadingIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    background: A.surface,
    borderRadius: '16px',
    border: '1px solid #e2e8f0',
    alignSelf: 'flex-start',
    [DARK]: { borderColor: S.line },
  },
  loadingDots: {
    display: 'flex',
    gap: '4px',
    '& span': {
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      background: '#6366f1',
      animation: '$bounce 1.4s infinite ease-in-out',
      '&:nth-child(1)': { animationDelay: '0s' },
      '&:nth-child(2)': { animationDelay: '0.2s' },
      '&:nth-child(3)': { animationDelay: '0.4s' },
    },
  },
  loadingText: {
    fontSize: '13px',
    color: '#64748b',
    [DARK]: { color: S.muted },
  },
  '@keyframes bounce': {
    '0%, 80%, 100%': { transform: 'translateY(0)' },
    '40%': { transform: 'translateY(-6px)' },
  },
  // Input area
  inputContainer: {
    padding: '16px 20px',
    borderTop: '1px solid #e2e8f0',
    background: A.surface,
    [DARK]: { borderTopColor: S.line },
  },
  inputRow: {
    display: 'flex',
    gap: '12px',
  },
  textInput: {
    flex: 1,
    padding: '12px 16px',
    fontSize: '14px',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    outline: 'none',
    resize: 'none',
    fontFamily: 'inherit',
    transition: 'border-color 0.2s ease',
    '&:focus': {
      borderColor: '#6366f1',
    },
    '&::placeholder': {
      color: '#94a3b8',
    },
    [DARK]: {
      background: S.field,
      color: S.fg,
      borderColor: S.fieldLine,
      '&:focus': { borderColor: S.blue },
      '&::placeholder': { color: S.muted },
    },
  },
  sendButton: {
    padding: '12px',
    background: '#6366f1',
    color: '#ffffff',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.2s ease',
    '&:hover:not(:disabled)': {
      background: '#4f46e5',
    },
    '&:disabled': {
      background: '#cbd5e1',
      cursor: 'not-allowed',
    },
    '& .material-icons': {
      fontSize: '22px',
    },
    [DARK]: { '&:disabled': { background: S.fieldLine } },
  },
  stopButton: {
    padding: '12px',
    background: '#ef4444',
    color: '#ffffff',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.2s ease',
    '&:hover': {
      background: '#dc2626',
    },
    '& .material-icons': {
      fontSize: '22px',
    },
  },
  disclaimer: {
    marginTop: '8px',
    fontSize: '11px',
    color: '#94a3b8',
    textAlign: 'center',
    [DARK]: { color: S.muted },
  },
  // Image upload styles
  imageUploadContainer: {
    marginBottom: '8px',
  },
  imageUploadRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  imageUploadButton: {
    padding: '8px 12px',
    fontSize: '12px',
    background: '#f1f5f9',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    transition: 'all 0.2s ease',
    color: '#64748b',
    '&:hover': {
      background: '#e2e8f0',
      borderColor: '#cbd5e1',
    },
    '& .material-icons': {
      fontSize: '16px',
    },
    [DARK]: {
      background: S.chrome,
      borderColor: S.line,
      color: S.muted,
      '&:hover': { background: S.chromeHover, borderColor: S.fieldLine },
    },
  },
  imageUploadHint: {
    fontSize: '11px',
    color: '#94a3b8',
    marginLeft: 'auto',
    [DARK]: { color: S.muted },
  },
  imagePreviews: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '8px',
  },
  imagePreviewItem: {
    position: 'relative',
    width: '60px',
    height: '60px',
    borderRadius: '8px',
    overflow: 'hidden',
    border: '1px solid #e2e8f0',
    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
    [DARK]: { borderColor: S.line },
  },
  imagePreviewRemove: {
    position: 'absolute',
    top: '2px',
    right: '2px',
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    background: 'rgba(0, 0, 0, 0.6)',
    color: '#ffffff',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& .material-icons': {
      fontSize: '12px',
    },
    '&:hover': {
      background: 'rgba(0, 0, 0, 0.8)',
    },
  },
  messageImages: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
    marginTop: '8px',
  },
  messageImageThumb: {
    width: '48px',
    height: '48px',
    borderRadius: '6px',
    overflow: 'hidden',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
  },
  imageDescriptionNote: {
    fontSize: '11px',
    color: '#94a3b8',
    fontStyle: 'italic',
    marginTop: '4px',
    [DARK]: { color: S.muted },
  },
  analyzingStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    background: '#fef3c7',
    borderRadius: '8px',
    fontSize: '12px',
    color: '#92400e',
    marginBottom: '8px',
    '& .material-icons': {
      fontSize: '16px',
      animation: '$spin 1s linear infinite',
    },
    [DARK]: { background: TONES.amber.bg, color: TONES.amber.fg },
  },
  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
  // Drag and drop overlay
  dropZone: {
    position: 'relative',
  },
  dropOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'rgba(99, 102, 241, 0.1)',
    border: '2px dashed #6366f1',
    borderRadius: '12px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
    pointerEvents: 'none',
  },
  dropOverlayIcon: {
    fontSize: '48px',
    color: '#6366f1',
    marginBottom: '8px',
  },
  dropOverlayText: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#6366f1',
  },
  dropOverlayHint: {
    fontSize: '12px',
    color: '#94a3b8',
    marginTop: '4px',
    [DARK]: { color: S.muted },
  },
  // Clear chat button
  clearChatSection: {
    padding: '8px 20px',
    borderTop: '1px solid #e2e8f0',
    textAlign: 'center',
    [DARK]: { borderTopColor: S.line },
  },
  clearChatButton: {
    padding: '6px 16px',
    fontSize: '12px',
    background: 'transparent',
    border: '1px solid #e2e8f0',
    borderRadius: '16px',
    cursor: 'pointer',
    color: '#64748b',
    transition: 'all 0.2s ease',
    '&:hover': {
      background: '#f1f5f9',
      borderColor: '#cbd5e1',
    },
    [DARK]: {
      borderColor: S.line,
      color: S.muted,
      '&:hover': { background: S.chromeHover, borderColor: S.fieldLine },
    },
  },
});

interface Message {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  isError?: boolean;
  code?: {
    html?: string;
    css?: string;
    js?: string;
  };
  // Images attached to user message (for display only)
  images?: Array<{ data: string; media_type: string; preview: string }>;
}

interface ImageFile {
  file: File;
  preview: string;
  data: string;  // Base64 encoded
  media_type: string;
}

interface AiChatPanelProps {
  existingCode: {
    html: string;
    css: string;
    js: string;
  };
  onApplyCode: (type: 'html' | 'css' | 'js', code: string) => void;
}

const EXAMPLE_PROMPTS = [
  'Create a hero section with animated text',
  'Add a responsive image gallery',
  'Build a testimonial carousel',
  'Make a call-to-action banner',
];

const AiChatPanel: React.FC<AiChatPanelProps> = ({ existingCode, onApplyCode }) => {
  const c = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isClearingRef = useRef(false); // Track if we're clearing to avoid showing cancelled message
  
  // Image handling state
  const [selectedImages, setSelectedImages] = useState<ImageFile[]>([]);
  const [imageDescriptions, setImageDescriptions] = useState<string | null>(null);
  const [isAnalyzingImages, setIsAnalyzingImages] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0); // Track drag enter/leave for nested elements
  
  // Keep a ref to the latest code to ensure we always send the current state
  const existingCodeRef = useRef(existingCode);
  useEffect(() => {
    existingCodeRef.current = existingCode;
  }, [existingCode]);

  // Auto-scroll to bottom when messages change
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Cleanup: abort any pending request when component unmounts
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [input]);

  // Convert file to base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        // Remove the data:image/xxx;base64, prefix
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  // Handle image selection
  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const maxImages = 5; // Limit to 5 images
    const maxSizeBytes = 5 * 1024 * 1024; // 5MB per image

    const newImages: ImageFile[] = [];
    
    for (let i = 0; i < Math.min(files.length, maxImages - selectedImages.length); i++) {
      const file = files[i];
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        continue;
      }
      
      // Validate file size
      if (file.size > maxSizeBytes) {
        alert(`Image "${file.name}" is too large. Maximum size is 5MB.`);
        continue;
      }

      try {
        const base64 = await fileToBase64(file);
        const preview = URL.createObjectURL(file);
        
        newImages.push({
          file,
          preview,
          data: base64,
          media_type: file.type,
        });
      } catch (err) {
        console.error('Error processing image:', err);
      }
    }

    setSelectedImages((prev) => [...prev, ...newImages].slice(0, maxImages));
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Remove selected image
  const handleRemoveImage = (index: number) => {
    setSelectedImages((prev) => {
      const updated = [...prev];
      // Revoke object URL to free memory
      URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
  };

  // Drag and drop handlers
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounterRef.current = 0;

    if (loading) return;

    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    const maxImages = 5;
    const maxSizeBytes = 5 * 1024 * 1024; // 5MB per image

    const newImages: ImageFile[] = [];
    
    for (let i = 0; i < Math.min(files.length, maxImages - selectedImages.length); i++) {
      const file = files[i];
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        continue;
      }
      
      // Validate file size
      if (file.size > maxSizeBytes) {
        alert(`Image "${file.name}" is too large. Maximum size is 5MB.`);
        continue;
      }

      try {
        const base64 = await fileToBase64(file);
        const preview = URL.createObjectURL(file);
        
        newImages.push({
          file,
          preview,
          data: base64,
          media_type: file.type,
        });
      } catch (err) {
        console.error('Error processing dropped image:', err);
      }
    }

    if (newImages.length > 0) {
      setSelectedImages((prev) => [...prev, ...newImages].slice(0, maxImages));
    }
  }, [loading, selectedImages.length]);

  // Cleanup image previews on unmount
  // Note: Using selectedImages in the cleanup function via a ref pattern
  // to ensure we always have access to the current state during cleanup
  const selectedImagesRef = useRef(selectedImages);
  selectedImagesRef.current = selectedImages;
  
  useEffect(() => {
    return () => {
      selectedImagesRef.current.forEach((img) => URL.revokeObjectURL(img.preview));
    };
  }, []);

  // Parse code blocks from response
  const parseCodeBlocks = (text: string) => {
    const code: { html?: string; css?: string; js?: string } = {};
    
    // Extract HTML
    const htmlMatch = text.match(/```html\s*([\s\S]*?)```/i);
    if (htmlMatch) code.html = htmlMatch[1].trim();
    
    // Extract CSS
    const cssMatch = text.match(/```css\s*([\s\S]*?)```/i);
    if (cssMatch) code.css = cssMatch[1].trim();
    
    // Extract JavaScript
    const jsMatch = text.match(/```(?:javascript|js)\s*([\s\S]*?)```/i);
    if (jsMatch) code.js = jsMatch[1].trim();
    
    return Object.keys(code).length > 0 ? code : undefined;
  };

  // Remove code blocks from text for display
  const stripCodeBlocks = (text: string) => {
    return text
      .replace(/```html\s*[\s\S]*?```/gi, '')
      .replace(/```css\s*[\s\S]*?```/gi, '')
      .replace(/```(?:javascript|js)\s*[\s\S]*?```/gi, '')
      .trim();
  };

  const handleSend = async (messageText = input) => {
    if (!messageText.trim() || loading) return;

    // Capture images to send with this message
    const imagesToSend = selectedImages.length > 0 ? [...selectedImages] : null;
    
    const userMessage: Message = {
      id: Date.now(),
      role: 'user',
      content: messageText.trim(),
      timestamp: new Date(),
      // Store image previews for display (only on first message with images)
      images: imagesToSend?.map((img) => ({
        data: img.data,
        media_type: img.media_type,
        preview: img.preview,
      })),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    
    // Clear selected images after sending (they'll be converted to descriptions)
    if (imagesToSend) {
      setIsAnalyzingImages(true);
      setSelectedImages([]);
    }

    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();

    try {
      // Get base URL - use APP_URL from Genesis settings or default
      const baseUrl = getAppURL() || '';
      const apiUrl = `${baseUrl}/api/widget-agent/chat`;

      // Get the latest code from the ref to ensure we always send current state
      const currentCode = existingCodeRef.current;
      
      // Build request payload
      // Key strategy: Send raw images only on first turn, then use descriptions
      const requestPayload: Record<string, unknown> = {
        message: messageText.trim(),
        conversation_id: conversationId,
        conversation_history: messages.map((m) => ({
          role: m.role,
          content: m.content,
        })),
        existing_code: {
          html: currentCode.html || '',
          css: currentCode.css || '',
          js: currentCode.js || '',
        },
      };

      // If we have images to send (first turn with images)
      if (imagesToSend && imagesToSend.length > 0) {
        requestPayload.images = imagesToSend.map((img) => ({
          data: img.data,
          media_type: img.media_type,
        }));
      } else if (imageDescriptions) {
        // If we have stored image descriptions (subsequent turns)
        // Send the descriptions instead of raw images (saves tokens!)
        requestPayload.image_descriptions = imageDescriptions;
      }
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestPayload),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const responseContent = data.response;
      const extractedCode = data.code || parseCodeBlocks(responseContent);

      // Store image descriptions from response for future turns
      // This is the "reference many" part of our strategy
      if (data.image_descriptions) {
        setImageDescriptions(data.image_descriptions);
      }

      const assistantMessage: Message = {
        id: Date.now() + 1,
        role: 'assistant',
        content: extractedCode ? stripCodeBlocks(responseContent) : responseContent,
        timestamp: new Date(),
        code: extractedCode,
      };

      setMessages((prev) => [...prev, assistantMessage]);
      setConversationId(data.conversation_id);
    } catch (error) {
      // Don't show any message if we're clearing the chat
      if (isClearingRef.current) {
        return;
      }
      // Show cancelled message if manually stopped (not clearing)
      if (error instanceof Error && error.name === 'AbortError') {
        const cancelledMessage: Message = {
          id: Date.now() + 1,
          role: 'assistant',
          content: 'Request cancelled.',
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, cancelledMessage]);
      } else {
        console.error('Chat error:', error);
        const errorMessage: Message = {
          id: Date.now() + 1,
          role: 'assistant',
          content: `Sorry, I encountered an error: ${error instanceof Error ? error.message : 'Unknown error'}. Please make sure the AI Widget Agent service is running.`,
          timestamp: new Date(),
          isError: true,
        };
        setMessages((prev) => [...prev, errorMessage]);
      }
    } finally {
      setLoading(false);
      setIsAnalyzingImages(false);
      abortControllerRef.current = null;
    }
  };

  const handleStop = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  // Note: Enter does NOT submit to prevent accidental submissions
  // Click the Send button to submit your message

  const handleExampleClick = (prompt: string) => {
    setInput(prompt);
    handleSend(prompt);
  };

  const handleClearChat = () => {
    // Set flag to prevent "cancelled" message from appearing
    isClearingRef.current = true;
    // Abort any pending request before clearing
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setMessages([]);
    setConversationId(null);
    setLoading(false);
    // Clear image-related state
    selectedImages.forEach((img) => URL.revokeObjectURL(img.preview));
    setSelectedImages([]);
    setImageDescriptions(null);
    setIsAnalyzingImages(false);
    // Reset flag after a tick
    setTimeout(() => {
      isClearingRef.current = false;
    }, 0);
  };

  const handleApplyCode = (type: 'html' | 'css' | 'js', code: string) => {
    onApplyCode(type, code);
  };

  const renderCodeBlock = (language: string, code: string, type: 'html' | 'css' | 'js') => (
    <div className={c.codeBlockContainer}>
      <div className={c.codeBlockHeader}>
        <span className={c.codeBlockLang}>{language}</span>
        <div className={c.codeBlockActions}>
          <button
            className={c.applyButton}
            onClick={() => handleApplyCode(type, code)}
          >
            <i className="material-icons">check</i>
            Apply
          </button>
        </div>
      </div>
      <div className={c.codeBlockBody}>
        <pre>{code}</pre>
      </div>
    </div>
  );

  return (
    <>
      {/* Floating Button */}
      <button
        className={c.floatingButton}
        onClick={() => setIsOpen(true)}
        title="AI Widget Builder"
      >
        <i className="material-icons">auto_awesome</i>
      </button>

      {/* Overlay */}
      <div
        className={`${c.overlay} ${isOpen ? c.overlayOpen : ''}`}
        onClick={() => setIsOpen(false)}
        style={{ visibility: isOpen ? 'visible' : 'hidden' }}
      />

      {/* Drawer - inline styles prevent flash before JSS styles are injected */}
      <div 
        className={`${c.drawer} ${isOpen ? c.drawerOpen : ''}`}
        style={{
          visibility: isOpen ? 'visible' : 'hidden',
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
        }}
      >
        {/* Header */}
        <div className={c.header}>
          <div className={c.headerTitle}>
            <div className={c.headerIcon}>
              <i className="material-icons">auto_awesome</i>
            </div>
            <div className={c.headerText}>
              <h2>AI Widget Builder</h2>
              <span>Create beautiful widgets with AI</span>
            </div>
          </div>
          <button className={c.closeButton} onClick={() => setIsOpen(false)}>
            <i className="material-icons">close</i>
          </button>
        </div>

        {/* Messages */}
        <div className={c.messagesContainer}>
          {messages.length === 0 ? (
            <div className={c.welcomeMessage}>
              <i className="material-icons">auto_awesome</i>
              <h3>Hello! I&apos;m your AI Widget Builder</h3>
              <p>
                I can help you create and refine custom widgets with HTML, CSS, and JavaScript.
                Describe what you want to build and I&apos;ll generate production-ready code.
              </p>
              <div className={c.exampleChips}>
                {EXAMPLE_PROMPTS.map((prompt, index) => (
                  <button
                    key={index}
                    className={c.exampleChip}
                    onClick={() => handleExampleClick(prompt)}
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`${c.messageBubble} ${
                    message.role === 'user' ? c.userMessage : c.assistantMessage
                  } ${message.isError ? c.errorMessage : ''}`}
                >
                  <div className={c.messageContent}>
                    <ReactMarkdown>{message.content}</ReactMarkdown>
                    {/* Show attached images on user messages */}
                    {message.images && message.images.length > 0 && (
                      <div className={c.messageImages}>
                        {message.images.map((img, idx) => (
                          <div key={idx} className={c.messageImageThumb}>
                            <img 
                              src={img.preview || `data:${img.media_type};base64,${img.data}`} 
                              alt={`Attached ${idx + 1}`} 
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {message.code && (
                    <>
                      {message.code.html && renderCodeBlock('HTML', message.code.html, 'html')}
                      {message.code.css && renderCodeBlock('CSS', message.code.css, 'css')}
                      {message.code.js && renderCodeBlock('JavaScript', message.code.js, 'js')}
                    </>
                  )}
                </div>
              ))}
              {loading && (
                <div className={c.loadingIndicator}>
                  <div className={c.loadingDots}>
                    <span />
                    <span />
                    <span />
                  </div>
                  <span className={c.loadingText}>Generating code...</span>
                </div>
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Clear chat */}
        {messages.length > 0 && (
          <div className={c.clearChatSection}>
            <button className={c.clearChatButton} onClick={handleClearChat}>
              Clear conversation
            </button>
          </div>
        )}

        {/* Input - Drop zone for images */}
        <div 
          className={`${c.inputContainer} ${c.dropZone}`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          {/* Drag overlay */}
          {isDragging && (
            <div className={c.dropOverlay}>
              <i className={`material-icons ${c.dropOverlayIcon}`}>cloud_upload</i>
              <span className={c.dropOverlayText}>Drop images here</span>
              <span className={c.dropOverlayHint}>PNG, JPEG, GIF, WebP (max 5MB each)</span>
            </div>
          )}
          
          {/* Image upload section */}
          <div className={c.imageUploadContainer}>
            <div className={c.imageUploadRow}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageSelect}
                style={{ display: 'none' }}
                disabled={loading}
              />
              <button
                className={c.imageUploadButton}
                onClick={() => fileInputRef.current?.click()}
                disabled={loading || selectedImages.length >= 5}
                title="Attach images (max 5)"
              >
                <i className="material-icons">add_photo_alternate</i>
                Add Image
              </button>
              {imageDescriptions && (
                <span className={c.imageDescriptionNote}>
                  Using previously analyzed image context
                </span>
              )}
              <span className={c.imageUploadHint}>
                {selectedImages.length > 0 
                  ? `${selectedImages.length}/5 images`
                  : 'Drop images or click to add'}
              </span>
            </div>
            
            {/* Image previews */}
            {selectedImages.length > 0 && (
              <div className={c.imagePreviews}>
                {selectedImages.map((img, index) => (
                  <div key={index} className={c.imagePreviewItem}>
                    <img src={img.preview} alt={`Preview ${index + 1}`} />
                    <button
                      className={c.imagePreviewRemove}
                      onClick={() => handleRemoveImage(index)}
                      title="Remove image"
                    >
                      <i className="material-icons">close</i>
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Analyzing status */}
            {isAnalyzingImages && (
              <div className={c.analyzingStatus}>
                <i className="material-icons">refresh</i>
                Analyzing images for better widget generation...
              </div>
            )}
          </div>

          <div className={c.inputRow}>
            <textarea
              ref={textareaRef}
              className={c.textInput}
              placeholder="Describe the widget you want to create..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={loading}
              rows={1}
            />
            {loading ? (
              <button
                className={c.stopButton}
                onClick={handleStop}
                title="Stop generating"
              >
                <i className="material-icons">stop</i>
              </button>
            ) : (
              <button
                className={c.sendButton}
                onClick={() => handleSend()}
                disabled={!input.trim()}
                title="Send message"
              >
                <i className="material-icons">send</i>
              </button>
            )}
          </div>
          <p className={c.disclaimer}>
            AI can make mistakes. Review and test generated code before publishing.
          </p>
        </div>
      </div>
    </>
  );
};

export default AiChatPanel;

